import classNames from 'classnames';
import { Link as RouterLink } from 'react-router-dom';

import Icons from 'assets/icons';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import NavButton from 'containers/user/home/components/shared/navigation-button';
import { useCurrentUser } from 'hooks/api/use-current-user';

export interface ReportTileData {
  id: string | number;
  title: string;
  description?: string;
  hasDataIssue?: boolean;
  productType?: string;
  powerBiId?: string;
  powerBiReportType?: string;
  [key: string]: unknown;
}

interface ReportTileProps {
  report: ReportTileData;
  isOpen: boolean;
}

const ReportTile = ({ report, isOpen }: ReportTileProps): React.JSX.Element => {
  const { title, description, hasDataIssue } = report;
  const currentUser = useCurrentUser();
  const isUnavailable =
    !!hasDataIssue &&
    !(currentUser?.data as { internalUser?: boolean } | undefined)
      ?.internalUser;

  const tabIndex = isOpen ? 0 : -1;

  const tileClassName = classNames(
    'w-full h-full flex flex-col text-left border border-navy-200 rounded-md p-5 transition-all duration-200 no-underline',
    { 'hover:shadow-sm': !isUnavailable }
  );

  const titleContent = (
    <>
      <Box
        sx={{
          fontWeight: 600,
          mb: 0.5,
          color: isUnavailable ? 'tokens.gray.500' : 'tokens.gray.700',
        }}
      >
        {title}
      </Box>

      <Box
        component="p"
        sx={{
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          mb: 3,
          color: isUnavailable ? 'tokens.gray.500' : 'tokens.gray.600',
        }}
      >
        {description}
      </Box>
    </>
  );

  if (isUnavailable) {
    return (
      <div className={tileClassName}>
        {titleContent}

        <Box sx={{ mt: 'auto' }}>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', color: 'tokens.critical.600', mb: 1 }}
          >
            <Icons.Warning className="mr-2" />
            <span className="text-sm font-bold">Report Unavailable</span>
          </Stack>

          <NavButton
            text="Contact Client Success"
            link="mailto:clientsupport@marketviewedu.com"
          />
        </Box>
      </div>
    );
  }

  return (
    <RouterLink
      to={`/reports/${report.id}`}
      className={tileClassName}
      tabIndex={tabIndex}
    >
      {titleContent}
    </RouterLink>
  );
};

export default ReportTile;
